import {
  ChangeDetectionStrategy,
  Component,
  QueryList,
  ViewChildren,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiMultiselectComponent } from '../../atoms/multiselect/multiselect.component';
import { UiSelectComponent, UiSelectOption } from '../../atoms/select/select.component';
import { UiTooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { UiFilterMenuComponent } from '../../molecules/filter-menu/filter-menu.component';
import { UiSegmentedFilterComponent } from '../../molecules/segmented-filter/segmented-filter.component';
import type {
  DataTableSegmentedFilter,
  DataTableToolbarSelect,
  FilterOptionsModel,
} from './data-table.types';

@Component({
  selector: 'ui-data-table-toolbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    UiFilterMenuComponent,
    UiSegmentedFilterComponent,
    UiMultiselectComponent,
    UiSelectComponent,
    UiTooltipDirective,
    UiButtonComponent,
  ],
  templateUrl: './data-table-toolbar.component.html',
  styleUrl: './data-table-toolbar.component.scss',
})
export class UiDataTableToolbarComponent {
  @ViewChildren(UiMultiselectComponent) private readonly toolbarMultiselects!: QueryList<UiMultiselectComponent>;

  // Inputs (mismos datos que usaba el toolbar en el data-table)
  readonly title = input('');

  readonly showStatusFilter = input(false);
  readonly statusFilters = input<FilterOptionsModel[]>([]);
  readonly segmentedFilters = input<DataTableSegmentedFilter[]>([]);

  readonly toolbarSelects = input<DataTableToolbarSelect[]>([]);

  readonly pageSizeOptions = input<UiSelectOption[]>([]);
  readonly selectedSizeOption = input<UiSelectOption | null>(null);
  /** Muestra el select de registros por página. Default: true. */
  readonly showPageSize = input(true);

  readonly showNewButton = input(false);
  readonly newLabel = input('Nuevo');
  readonly showExcel = input(true);

  // Outputs → el padre re-emite sus outputs públicos
  readonly statusFiltersChange = output<FilterOptionsModel[]>();
  readonly segmentedFiltersChange = output<DataTableSegmentedFilter[]>();
  readonly toolbarSelectsChange = output<Record<string, UiSelectOption[]>>();
  readonly pageSizeChange = output<number>();
  readonly exportExcel = output<void>();
  readonly newClick = output<void>();

  // Estado interno: selección multiselect (key → opciones seleccionadas)
  private readonly tsSelected = signal<Record<string, UiSelectOption[]>>({});
  // Estado interno: selección single select (key → opción seleccionada)
  private readonly tsSingleSelected = signal<Record<string, UiSelectOption | null>>({});

  protected tsCurrentSelected = (key: string): UiSelectOption[] => {
    const current = this.tsSelected();
    if (Object.prototype.hasOwnProperty.call(current, key)) {
      return current[key] ?? [];
    }
    return this.toolbarSelects().find(select => select.key === key)?.selected ?? [];
  };

  protected tsSingleCurrentSelected = (key: string): UiSelectOption | null =>
    this.tsSingleSelected()[key] ?? null;

  protected onTsSelectionChange(key: string, selected: UiSelectOption[]): void {
    const updated: Record<string, UiSelectOption[]> = {
      ...Object.fromEntries(
        this.toolbarSelects().map(select => [
          select.key,
          select.selected ?? [],
        ]),
      ),
      ...this.tsSelected(),
      [key]: selected,
    };
    this.tsSelected.set(updated);
    this.toolbarSelectsChange.emit(updated);
  }

  protected onTsSingleSelectionChange(key: string, selected: UiSelectOption | null): void {
    this.tsSingleSelected.set({ ...this.tsSingleSelected(), [key]: selected });
  }

  /** Cierra todos los multiselects del toolbar EXCEPTO el que acaba de abrir. */
  protected closeOtherMultiselects(source: UiMultiselectComponent): void {
    this.toolbarMultiselects?.forEach((ms) => {
      if (ms !== source) ms.closePanel();
    });
  }

  protected closeAllMultiselects(): void {
    this.toolbarMultiselects?.forEach((ms) => ms.closePanel());
  }

  protected onFilterMenuChange(groups: FilterOptionsModel[]): void {
    this.statusFiltersChange.emit(groups);
  }

  protected onSegmentedFilterChange(key: string, value: string): void {
    this.segmentedFiltersChange.emit(
      this.segmentedFilters().map(group =>
        group.key === key ? { ...group, value } : group,
      ),
    );
  }

  protected onSizeChange(option: UiSelectOption | null): void {
    const next = Number(option?.value ?? option?.id ?? 10);
    this.pageSizeChange.emit(next);
    this.closeAllMultiselects();
  }

  protected onExportExcel(): void {
    this.exportExcel.emit();
    this.closeAllMultiselects();
  }

  protected onNew(): void {
    this.newClick.emit();
    this.closeAllMultiselects();
  }

  protected readonly hasToolbarSelects = computed(() => this.toolbarSelects().length > 0);
  protected readonly hasStatusFilter = computed(
    () => this.showStatusFilter() && this.statusFilters().length > 0,
  );
}

